// src/pages/ProgramDays.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Card, CardContent, IconButton, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Chip, useTheme
} from '@mui/material';
import { ArrowBack, Add, DragIndicator, Visibility, Edit, Delete } from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminHeader from '../components/AdminHeader';
import programAPI from '../services/programApi';
import { dayAPI } from '../services/api';
import DayForm from '../components/DayForm';

// ================== Sortable ден компонент ==================
const SortableDayItem = ({ day, onView, onEdit, onDelete }) => {
    const theme = useTheme();
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: day.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <Card ref={setNodeRef} style={style} sx={{
            borderRadius: '20px',
            backgroundColor: theme.palette.background.paper,
            border: '1px solid rgba(255,126,185,0.2)',
            boxShadow: '0 4px 24px rgba(255,126,185,0.08)',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            cursor: 'grab',
            '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: '0 16px 48px rgba(255,126,185,0.25)',
                borderColor: 'rgba(255,126,185,0.5)'
            },
            '&:active': { cursor: 'grabbing' }
        }}>
            {/* ✅ Accent bar na vrv */}
            <Box sx={{
                height: 5,
                background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)',
                flexShrink: 0
            }} />
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Drag handle */}
                    <IconButton
                        {...attributes}
                        {...listeners}
                        sx={{
                            color: '#ff7eb9', cursor: 'grab', mt: 0.5,
                            '&:hover': { backgroundColor: 'rgba(255,126,185,0.1)' },
                            '&:active': { cursor: 'grabbing' }
                        }}
                    >
                        <DragIndicator />
                    </IconButton>

                    {/* Info */}
                    <Box sx={{ flex: 1 }}>
                        {/* Chip */}
                        <Chip label="Ден" size="small" sx={{
                            bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9',
                            fontWeight: 'bold', fontSize: '0.7rem',
                            border: '1px solid rgba(255,126,185,0.3)', mb: 2
                        }} />

                        {/* Title */}
                        <Typography variant="h5" sx={{
                            fontWeight: 800,
                            color: theme.palette.text.primary,
                            fontSize: { xs: '1.2rem', md: '1.4rem' },
                            lineHeight: 1.3, mb: 1
                        }}>
                            📅 {day.title || day.name}
                        </Typography>

                        {/* Divider */}
                        <Box sx={{
                            width: 40, height: 3, borderRadius: 2,
                            background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)',
                            mb: 2
                        }} />

                        {/* Description */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.7,
                                mb: 2,
                                whiteSpace: 'pre-wrap',   // ✅ ги чува \n и празни линии
                                wordBreak: 'break-word'
                            }}
                        >
                            {day.description || 'Нема опис'}
                        </Typography>

                        {/* Stats chips */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Chip
                                label={`📋 Ден ${day.orderIndex}`}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(165,216,255,0.15)',
                                    color: '#a5d8ff',
                                    fontWeight: 'bold',
                                    border: '1px solid rgba(165,216,255,0.3)'
                                }}
                            />
                            <Chip
                                label={`🎥 ${day.videos?.length || 0} видеа`}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(171,71,188,0.08)',
                                    color: '#AB47BC',
                                    fontWeight: 'bold',
                                    border: '1px solid rgba(171,71,188,0.2)'
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <IconButton
                            onClick={e => { e.stopPropagation(); onView(day); }}
                            title="Види видеа"
                            sx={{
                                color: '#4CAF50',
                                '&:hover': { backgroundColor: 'rgba(76,175,80,0.1)', transform: 'scale(1.1)' },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <Visibility />
                        </IconButton>
                        <IconButton
                            onClick={e => { e.stopPropagation(); onEdit(day); }}
                            title="Промени ден"
                            sx={{
                                color: '#a5d8ff',
                                '&:hover': { backgroundColor: 'rgba(165,216,255,0.15)', transform: 'scale(1.1)' },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <Edit />
                        </IconButton>
                        <IconButton
                            onClick={e => { e.stopPropagation(); onDelete(day); }}
                            title="Избриши ден"
                            sx={{
                                color: '#ff6b6b',
                                '&:hover': { backgroundColor: 'rgba(255,107,107,0.1)', transform: 'scale(1.1)' },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <Delete />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// ================== Главен компонент ==================
const ProgramDays = () => {
    const { programId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme(); // ✅ useTheme za pozadina

    const [program, setProgram] = useState(null);
    const [allDays, setAllDays] = useState([]);
    const [programDays, setProgramDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addDayDialog, setAddDayDialog] = useState(false);
    const [editDayDialog, setEditDayDialog] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, day: null });

    useEffect(() => { fetchData(); }, [programId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [programData, daysData] = await Promise.all([
                programAPI.admin.getById(programId),
                dayAPI.admin.getAll()
            ]);
            setProgram(programData);
            setAllDays(daysData);
            setProgramDays(programData.days || []);
        } catch (err) {
            console.error(err);
            showSnackbar('Грешка при вчитување на податоците', 'error');
        } finally { setLoading(false); }
    };

    const showSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = programDays.findIndex(d => d.id === active.id);
            const newIndex = programDays.findIndex(d => d.id === over.id);
            const newDays = arrayMove(programDays, oldIndex, newIndex);
            setProgramDays(newDays);
            try {
                await dayAPI.admin.reorder(newDays.map(d => d.id));
                showSnackbar('Редоследот на деновите е зачуван');
            } catch (err) {
                showSnackbar('Грешка при зачувување на редоследот', 'error');
                fetchData();
            }
        }
    };

    const handleCreateNewDay = async (dayData) => {
        try {
            const newDay = await dayAPI.admin.create({
                title: dayData.title,
                description: dayData.description,
                orderIndex: allDays.length + 1
            });
            const updatedProgram = await programAPI.admin.addDay(programId, newDay.id);
            setProgram(updatedProgram);
            setProgramDays(updatedProgram.days || []);
            setAllDays(await dayAPI.admin.getAll());
            showSnackbar('Денот е успешно креиран 🎉');
            setAddDayDialog(false);
        } catch (err) {
            showSnackbar('Грешка при креирање на денот', 'error');
        }
    };

    const handleUpdateDay = async (dayData) => {
        try {
            await dayAPI.admin.update(selectedDay.id, {
                title: dayData.title,
                description: dayData.description,
                orderIndex: selectedDay.orderIndex || 1
            });
            await fetchData();
            showSnackbar('Денот е успешно променет 🎉');
            setEditDayDialog(false);
            setSelectedDay(null);
        } catch (err) {
            showSnackbar('Грешка при промена на денот', 'error');
        }
    };

    const handleConfirmDelete = async () => {
        const day = deleteDialog.day;
        if (!day?.id) {
            showSnackbar('Грешка: Денот нема ID', 'error');
            setDeleteDialog({ open: false, day: null });
            return;
        }
        try {
            await programAPI.admin.removeDay(programId, day.id);
            setProgramDays(prev => prev.filter(d => d.id !== day.id));
            showSnackbar('Денот е успешно избришан 🗑️');
        } catch (err) {
            showSnackbar('Грешка при бришење на денот', 'error');
        } finally {
            setDeleteDialog({ open: false, day: null });
        }
    };

    // ===== LOADING =====
    if (loading) return (
        <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            <Box sx={{ px: 4, pt: 4 }}><AdminHeader /></Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Typography variant="h4" sx={{ color: '#ff7eb9', fontWeight: 'bold' }}>
                    ⏳ Вчитување...
                </Typography>
            </Box>
        </Box>
    );

    // ===== NOT FOUND =====
    if (!program) return (
        <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default, textAlign: 'center' }}>
            <Box sx={{ px: 4, pt: 4 }}><AdminHeader /></Box>
            <Typography variant="h4" sx={{ color: '#ff7eb9', mb: 2, mt: 10 }}>
                Програмата не е пронајдена
            </Typography>
            <Button onClick={() => navigate('/admin/programs')} variant="contained"
                    sx={{ background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', color: '#fff' }}>
                Назад кон програми
            </Button>
        </Box>
    );

    return (
        <>
            {/* ✅ theme.palette.background.default - se menuva so tema */}
            <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
                <Box sx={{ px: 4 }}><AdminHeader /></Box>

                <Box sx={{ px: 4 }}>
                    {/* ===== HEADER ===== */}
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                onClick={() => navigate('/admin/programs')}
                                startIcon={<ArrowBack />}
                                variant="contained"
                                sx={{
                                    bgcolor: '#a5d8ff', color: '#2b2b2b', fontWeight: 'bold',
                                    borderRadius: '12px', px: 3, py: 1,
                                    boxShadow: '0 4px 15px rgba(165,216,255,0.3)',
                                    '&:hover': { bgcolor: '#74c0fc', transform: 'translateY(-2px)' },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Назад кон програми
                            </Button>
                            <Box>
                                <Typography variant="h3" sx={{
                                    fontWeight: 900,
                                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontSize: { xs: '1.8rem', md: '2.5rem' }
                                }}>
                                    📋 {program.name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                                    {programDays.length} денови во програмата
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            onClick={() => { setSelectedDay(null); setAddDayDialog(true); }}
                            startIcon={<Add />}
                            sx={{
                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                color: '#fff', fontWeight: 'bold',
                                px: 4, py: 1.5, borderRadius: '12px',
                                boxShadow: '0 4px 15px rgba(255,126,185,0.3)',
                                textTransform: 'none', fontSize: '1rem',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #ff4a97, #9C27B0)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(255,126,185,0.4)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            Креирај нов ден
                        </Button>
                    </Box>

                    {/* ===== NO DAYS ===== */}
                    {(!programDays || programDays.length === 0) ? (
                        <Box sx={{
                            textAlign: 'center', py: 12,
                            border: '2px dashed rgba(255,126,185,0.3)',
                            borderRadius: '20px',
                            backgroundColor: 'rgba(255,126,185,0.03)'
                        }}>
                            <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>📭</Typography>
                            <Typography variant="h4" sx={{ color: '#ff7eb9', mb: 2, fontWeight: 'bold' }}>
                                Нема денови
                            </Typography>
                            <Typography variant="h6" sx={{
                                color: theme.palette.text.secondary, mb: 4,
                                maxWidth: 500, mx: 'auto'
                            }}>
                                Додајте го првиот ден за оваа програма
                            </Typography>
                            <Button
                                onClick={() => { setSelectedDay(null); setAddDayDialog(true); }}
                                variant="contained"
                                startIcon={<Add />}
                                sx={{
                                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                    color: '#fff', fontWeight: 'bold',
                                    px: 5, py: 1.5, borderRadius: '12px',
                                    fontSize: '1rem', textTransform: 'none',
                                    '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                                }}
                            >
                                Креирај прв ден
                            </Button>
                        </Box>
                    ) : (
                        /* ===== DAYS LIST ===== */
                        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={programDays.map(d => d.id)} strategy={verticalListSortingStrategy}>
                                <Grid container spacing={3}>
                                    {programDays.map(day => (
                                        <Grid item xs={12} key={day.id}>
                                            <SortableDayItem
                                                day={day}
                                                onView={(d) => navigate(`/admin/days/${d.id}`)}
                                                onEdit={(d) => { setSelectedDay(d); setEditDayDialog(true); }}
                                                onDelete={(d) => setDeleteDialog({ open: true, day: d })}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </SortableContext>
                        </DndContext>
                    )}
                </Box>
            </Box>

            {/* ===== SNACKBAR ===== */}
            <Snackbar open={snackbar.open} autoHideDuration={4000}
                      onClose={() => setSnackbar({ ...snackbar, open: false })}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity}
                       onClose={() => setSnackbar({ ...snackbar, open: false })}
                       sx={{ borderRadius: '12px', fontWeight: 'bold' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* ===== DAY FORMS ===== */}
            <DayForm
                open={addDayDialog}
                onSave={handleCreateNewDay}
                onCancel={() => setAddDayDialog(false)}
            />
            <DayForm
                open={editDayDialog}
                initialData={selectedDay}
                onSave={handleUpdateDay}
                onCancel={() => { setEditDayDialog(false); setSelectedDay(null); }}
            />

            {/* ===== DELETE DIALOG ===== */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, day: null })}
                    maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: '16px', border: '2px solid #ff7eb9' } }}>
                <DialogTitle sx={{
                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                    color: 'white', textAlign: 'center', fontWeight: 'bold'
                }}>
                    ⚠️ Потврда за бришење
                </DialogTitle>
                <DialogContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontWeight: 'bold', mt: 1 }}>
                        Дали сте сигурни дека сакате да го избришете денот
                    </Typography>
                    <Typography sx={{ color: '#ff4a97', fontWeight: 'bold', fontSize: '1.1rem', my: 1 }}>
                        "{deleteDialog.day?.title || deleteDialog.day?.name}"?
                    </Typography>
                    <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
                        Ова ќе го избрише денот и сите негови видеа!
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, day: null })}
                            variant="outlined"
                            sx={{
                                borderColor: '#ff6b6b', color: '#ff6b6b', px: 3,
                                borderRadius: '10px', fontWeight: 'bold',
                                '&:hover': { backgroundColor: 'rgba(255,107,107,0.1)' }
                            }}>
                        Откажи
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained"
                            sx={{
                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                color: '#fff', px: 3, borderRadius: '10px', fontWeight: 'bold',
                                '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                            }}>
                        Избриши
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ProgramDays;